import { Controller } from "@hotwired/stimulus"

// Pure Tailwind sidebar controller - no Bootstrap dependencies
export default class extends Controller {
  static targets = ["sidebar", "content", "toggleIcon", "overlay"]

  connect() {
    // Force the toggle icon to show correctly from the start
    if (this.hasToggleIconTarget) {
      // Set initial icon correctly
      this.toggleIconTarget.classList.add("fa-chevron-left")
      this.toggleIconTarget.classList.remove("fa-chevron-right")
    }

    // Check if sidebar state is stored in localStorage
    const sidebarCollapsed = localStorage.getItem("sidebarCollapsed") === "true"
    if (sidebarCollapsed) {
      this.collapse()
    }

    // Handle window resize events
    this.handleResponsiveLayout = this.handleResponsiveLayout.bind(this)
    window.addEventListener("resize", this.handleResponsiveLayout)

    // Initial check for responsive layout
    this.handleResponsiveLayout()

    // Restore sidebar scroll position after navigation
    this._restoreScrollPosition()

    // Save scroll position before Turbo navigations
    this._saveScrollOnNavigation = this._saveScrollPosition.bind(this)
    document.addEventListener("turbo:before-visit", this._saveScrollOnNavigation)
  }

  disconnect() {
    window.removeEventListener("resize", this.handleResponsiveLayout)
    document.removeEventListener("turbo:before-visit", this._saveScrollOnNavigation)
  }

  // Toggle sidebar expanded/collapsed state
  toggle(event) {
    if (event) event.preventDefault()

    if (this.isCollapsed()) {
      this.expand()
    } else {
      this.collapse()
    }
  }

  // Automatically expand the sidebar if it's collapsed when menu item is clicked
  expandIfCollapsed(event) {
    // Don't prevent default so the link navigation still works
    if (this.isCollapsed()) {
      this.expand()
    }
  }

  // Toggle mobile sidebar visibility (for all layouts)
  // Uses admin-mobile-visible class which CSS expects
  toggleMobile(event) {
    if (event) event.preventDefault()

    // Only apply on mobile screens
    if (window.innerWidth >= 768) return

    this.sidebarTarget.classList.toggle("admin-mobile-visible")

    // Handle overlay
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.toggle("active")
    }

    // Lock body scroll when sidebar is open
    document.body.classList.toggle("sidebar-open")
  }

  // Alias for dashboard mobile toggle (same behavior now)
  toggleDashboardMobile(event) {
    this.toggleMobile(event)
  }

  // Collapse the sidebar
  collapse() {
    // Safety check
    if (!this.hasSidebarTarget) return

    // Set the sidebar to collapsed state - use "admin-sidebar-collapsed" class which CSS expects
    this.sidebarTarget.classList.add("admin-sidebar-collapsed")

    // Update content margin
    if (this.hasContentTarget) {
      this.contentTarget.classList.add("admin-content-collapsed")
    }

    // Force a layout reflow to ensure styles are applied before continuing
    void this.sidebarTarget.offsetWidth

    // Aggressively hide all text elements
    this._hideAllTextElements()

    // Hide dropdown arrows
    this._hideDropdownArrows()

    // Center all icons
    this._centerIcons()

    // Hide all dropdown menus
    this._hideDropdownMenus()

    // Update toggle icon - replace the entire element to ensure we get a right-pointing icon
    if (this.hasToggleIconTarget) {
      // Create a new icon element
      const newIcon = document.createElement('i');
      newIcon.className = 'fas fa-chevron-right text-white text-sm flex items-center justify-center';
      newIcon.dataset.sidebarPureTarget = 'toggleIcon';

      // Ensure proper centering with inline styles
      newIcon.style.display = 'flex';
      newIcon.style.alignItems = 'center';
      newIcon.style.justifyContent = 'center';
      newIcon.style.width = '100%';
      newIcon.style.height = '100%';

      // Replace the old icon with the new one
      this.toggleIconTarget.replaceWith(newIcon);

      // Update the target reference - not needed since targets will refresh
    }

    // Store state
    localStorage.setItem("sidebarCollapsed", "true")

    // Close any open dropdown menus
    this.closeDropdowns()
  }

  // Helper method to hide all text elements
  _hideAllTextElements() {
    if (!this.hasSidebarTarget) return

    const textElements = this.sidebarTarget.querySelectorAll("span:not(.sr-only), p, div > span")
    textElements.forEach(el => {
      // Skip the toggle icon
      if (el.closest("[data-sidebar-pure-target='toggleIcon']")) return

      // Skip collapsed text elements - these should be SHOWN when collapsed
      if (el.classList.contains("sidebar-collapsed-text")) {
        el.classList.remove("hidden")
        el.style.display = "inline"
        el.style.visibility = "visible"
        el.style.opacity = "1"
        el.style.width = "auto"
        el.style.height = "auto"
        el.style.overflow = "visible"
        el.style.position = "static"
        el.style.pointerEvents = "auto"
        return
      }

      // Apply multiple hiding techniques for maximum compatibility
      el.classList.add("hidden")
      el.style.display = "none"
      el.style.visibility = "hidden"
      el.style.opacity = "0"
      el.style.width = "0"
      el.style.height = "0"
      el.style.overflow = "hidden"
      el.style.position = "absolute"
      el.style.pointerEvents = "none"
    })
  }

  // Helper method to hide dropdown arrows
  _hideDropdownArrows() {
    if (!this.hasSidebarTarget) return

    const dropdownArrows = this.sidebarTarget.querySelectorAll(".fa-chevron-down")
    dropdownArrows.forEach(el => {
      el.classList.add("hidden")
      el.style.display = "none"
      el.style.visibility = "hidden"
    })
  }

  // Helper method to center icons
  _centerIcons() {
    if (!this.hasSidebarTarget) return

    const iconContainers = this.sidebarTarget.querySelectorAll("a, button")
    iconContainers.forEach(el => {
      // Skip the toggle button
      if (el.closest("[data-action='click->sidebar-pure#toggle']")) return

      el.classList.add("justify-center")
      el.style.justifyContent = "center"

      // Hide all divs inside navigation items except those with icons
      const innerDivs = el.querySelectorAll("div")
      innerDivs.forEach(div => {
        if (!div.querySelector("i")) {
          div.classList.add("hidden")
          div.style.display = "none"
          div.style.visibility = "hidden"
        }
      })

      const icon = el.querySelector("i:not(.fa-chevron-down)")
      if (icon) {
        icon.classList.remove("mr-3")
        icon.classList.add("mx-auto")
        icon.style.margin = "0 auto"
      }
    })
  }

  // Helper method to hide dropdown menus
  _hideDropdownMenus() {
    if (!this.hasSidebarTarget) return

    const dropdownMenus = this.sidebarTarget.querySelectorAll("[data-dropdown-pure-target='menu']")
    dropdownMenus.forEach(menu => {
      menu.classList.add("hidden")
      menu.style.display = "none"
      menu.style.visibility = "hidden"
    })
  }

  // Expand the sidebar
  expand() {
    // Safety check
    if (!this.hasSidebarTarget) return

    // Remove collapsed state - use "admin-sidebar-collapsed" class which CSS expects
    this.sidebarTarget.classList.remove("admin-sidebar-collapsed")

    // Update content margin
    if (this.hasContentTarget) {
      this.contentTarget.classList.remove("admin-content-collapsed")
    }

    // Force a layout reflow to ensure styles are applied before continuing
    void this.sidebarTarget.offsetWidth

    // Show all text elements
    this._showAllTextElements()

    // Show dropdown arrows
    this._showDropdownArrows()

    // Restore icon alignment
    this._restoreIconAlignment()
  }

  // Helper method to show all text elements
  _showAllTextElements() {
    if (!this.hasSidebarTarget) return

    const textElements = this.sidebarTarget.querySelectorAll("span:not(.sr-only), p, div > span")
    textElements.forEach(el => {
      // Hide collapsed text elements - these should be HIDDEN when expanded
      if (el.classList.contains("sidebar-collapsed-text")) {
        el.classList.add("hidden")
        el.style.display = "none"
        return
      }

      // Remove all hiding techniques applied previously
      el.classList.remove("hidden")
      el.style.removeProperty("display")
      el.style.removeProperty("visibility")
      el.style.removeProperty("opacity")
      el.style.removeProperty("width")
      el.style.removeProperty("height")
      el.style.removeProperty("overflow")
      el.style.removeProperty("position")
      el.style.removeProperty("pointer-events")
    })
  }

  // Helper method to show dropdown arrows
  _showDropdownArrows() {
    if (!this.hasSidebarTarget) return

    const dropdownArrows = this.sidebarTarget.querySelectorAll(".fa-chevron-down")
    dropdownArrows.forEach(el => {
      el.classList.remove("hidden")
      el.style.removeProperty("display")
      el.style.removeProperty("visibility")
    })
  }

  // Helper method to restore icon alignment
  _restoreIconAlignment() {
    if (!this.hasSidebarTarget) return

    const iconContainers = this.sidebarTarget.querySelectorAll("a, button")
    iconContainers.forEach(el => {
      // Skip the toggle button
      if (el.closest("[data-action='click->sidebar-pure#toggle']")) return

      el.classList.remove("justify-center")
      el.style.removeProperty("justify-content")

      // Show all divs inside navigation items
      const innerDivs = el.querySelectorAll("div")
      innerDivs.forEach(div => {
        div.classList.remove("hidden")
        div.style.removeProperty("display")
        div.style.removeProperty("visibility")
      })

      const icon = el.querySelector("i:not(.fa-chevron-down)")
      if (icon) {
        icon.classList.add("mr-3")
        icon.classList.remove("mx-auto")
        icon.style.removeProperty("margin")
      }
    })

    // Update toggle icon - replace the entire element to ensure we get a left-pointing icon
    if (this.hasToggleIconTarget) {
      // Create a new icon element
      const newIcon = document.createElement('i');
      newIcon.className = 'fas fa-chevron-left text-white text-sm flex items-center justify-center';
      newIcon.dataset.sidebarPureTarget = 'toggleIcon';

      // Ensure proper centering with inline styles
      newIcon.style.display = 'flex';
      newIcon.style.alignItems = 'center';
      newIcon.style.justifyContent = 'center';
      newIcon.style.width = '100%';
      newIcon.style.height = '100%';

      // Replace the old icon with the new one
      this.toggleIconTarget.replaceWith(newIcon);

      // Update the target reference - not needed since targets will refresh
    }

    // Store state
    localStorage.setItem("sidebarCollapsed", "false")
  }

  // Handle responsive layout changes
  handleResponsiveLayout() {
    // Safety check
    if (!this.hasSidebarTarget) return

    if (window.innerWidth < 768) {
      // Mobile: Hide sidebar by default but keep it expanded (not collapsed)
      this.sidebarTarget.classList.remove("admin-sidebar-collapsed")

      // Show all text elements for mobile
      this._showAllTextElements()

      // Show dropdown arrows for mobile
      this._showDropdownArrows()

      // Restore icon alignment for mobile
      this._restoreIconAlignment()

      // Ensure sidebar is hidden off-screen by default on mobile (unless opened)
      if (!this.sidebarTarget.classList.contains("admin-mobile-visible")) {
        this.sidebarTarget.classList.remove("transform-none")
      }

      // Ensure overlay is not active by default
      if (this.hasOverlayTarget && !this.sidebarTarget.classList.contains("admin-mobile-visible")) {
        this.overlayTarget.classList.remove("active")
        document.body.classList.remove("sidebar-open")
      }

      // Update content margin for mobile
      if (this.hasContentTarget) {
        this.contentTarget.classList.remove("admin-content-collapsed")
        this.contentTarget.classList.add("ml-0")
      }
    } else {
      // Desktop: Show sidebar based on previous state
      this.sidebarTarget.classList.add("transform-none")
      this.sidebarTarget.classList.remove("admin-mobile-visible")

      // Remove mobile-specific classes
      if (this.hasOverlayTarget) {
        this.overlayTarget.classList.remove("active")
      }
      document.body.classList.remove("sidebar-open")

      // Reset mobile margin
      if (this.hasContentTarget) {
        this.contentTarget.classList.remove("ml-0")
      }

      // Apply correct classes based on stored sidebar state
      if (localStorage.getItem("sidebarCollapsed") === "true") {
        this.collapse()
      } else {
        this.expand()
      }
    }
  }

  // Helper to check if sidebar is currently collapsed
  isCollapsed() {
    if (!this.hasSidebarTarget) return false
    return this.sidebarTarget.classList.contains("admin-sidebar-collapsed")
  }

  // Close all dropdown menus in the sidebar
  closeDropdowns() {
    if (!this.application) return

    // Find all sidebar dropdown controllers
    const dropdownControllers = this.application.controllers.filter(
      controller => controller.context.identifier === 'sidebar-dropdown-pure'
    )

    // Hide all dropdown menus
    dropdownControllers.forEach(controller => {
      if (typeof controller.hide === 'function') {
        controller.hide()
      }
    })
  }

  // Save sidebar scroll position to sessionStorage
  _saveScrollPosition() {
    if (!this.hasSidebarTarget) return
    sessionStorage.setItem("sidebarScrollTop", this.sidebarTarget.scrollTop)
  }

  // Restore sidebar scroll position from sessionStorage
  _restoreScrollPosition() {
    if (!this.hasSidebarTarget) return
    const scrollTop = sessionStorage.getItem("sidebarScrollTop")
    if (scrollTop !== null) {
      this.sidebarTarget.scrollTop = parseInt(scrollTop, 10)
    }
  }
}